import { fulfillmentShipmentTypeOrder, type FulfillmentApi } from '@entities/fulfillment'

import { rublesToKopecks } from './rubles'

/** Ступень в форме: порог берётся из списка, цена вводится строкой в рублях. */
export type ServicePriceLadderDraft = {
  volumeFrom: number
  /** Пусто — цена по запросу. */
  price: string
}

export type ServicePriceDraft = {
  serviceId: string
  isOffered: boolean
  ladder: ServicePriceLadderDraft[]
}

/** Прайс в форме: свой список услуг на каждую схему работы, которую отметил владелец. */
export type ServicePricesForm = Partial<Record<FulfillmentApi.ShipmentType, ServicePriceDraft[]>>

/** 1550 копеек → «15,50»; целые рубли остаются без хвоста. Пусто — цена по запросу. */
const kopecksToRubles = (kopecks: number | null) =>
  kopecks === null ? '' : (kopecks / 100).toFixed(2).replace(/\.00$/, '').replace('.', ',')

/**
 * Раскладывает прайс карточки в поля формы. Неоказываемые услуги сохраняются: сервер присылает все
 * услуги схемы, и владелец должен видеть их списком с галочками, а не терять из виду.
 */
export const toServicePricesForm = (rows: FulfillmentApi.ServicePriceRow[]): ServicePricesForm => {
  const values: ServicePricesForm = {}

  for (const row of rows) {
    const services = (values[row.shipment_type] ??= [])
    services.push({
      serviceId: row.service_id,
      isOffered: row.is_offered,
      ladder: [...row.price_ladder]
        .sort((one, other) => one.volume_from - other.volume_from)
        .map((step) => ({ volumeFrom: step.volume_from, price: kopecksToRubles(step.unit_price) })),
    })
  }

  return values
}

/**
 * Пересобирает прайс под отмеченные схемы работы: услуги в них одни и те же, а цена у каждой схемы
 * своя. Уже введённые цены переносятся — владелец мог заполнить их до того, как передумал со
 * схемой. Снятая схема остаётся в прайсе: отсев переезжает в `buildSaveServicePrices`, которая
 * отправляет строки только по отмеченным схемам.
 */
export const rebuildServicePrices = (
  current: ServicePricesForm,
  shipmentTypes: FulfillmentApi.ShipmentType[],
  services: FulfillmentApi.ServiceOption[]
): ServicePricesForm => {
  const rebuilt: ServicePricesForm = { ...current }

  for (const shipmentType of shipmentTypes) {
    rebuilt[shipmentType] = services.map(
      (service) =>
        current[shipmentType]?.find((item) => item.serviceId === service.id) ?? {
          serviceId: service.id,
          isOffered: false,
          ladder: [],
        }
    )
  }

  return rebuilt
}

const toPriceLadder = (
  draft: ServicePriceDraft,
  shipmentType: FulfillmentApi.ShipmentType
): FulfillmentApi.PriceStep[] => {
  // Услуга не оказывается — цен быть не может. У схемы «другое» лестницы нет вовсе: сервер
  // принимает там только пустой список.
  if (!draft.isOffered || shipmentType === 'other') {
    return []
  }

  return [...draft.ladder]
    .sort((one, other) => one.volumeFrom - other.volumeFrom)
    .map((step) => ({ volume_from: step.volumeFrom, unit_price: rublesToKopecks(step.price) }))
}

/**
 * Собирает прайс для отправки. Сервер заменяет прайс целиком, поэтому неоказываемые услуги нельзя
 * просто пропустить: пропуск читался бы как «оставить как было». Их отправляем с признаком
 * `is_offered: false` и пустой лестницей.
 *
 * В строке разрешены ровно четыре поля — название услуги и единицу измерения, которые сервер сам
 * прислал в ответе, отправлять обратно нельзя: он отклонит всю карточку.
 *
 * Отправляются только схемы работы, отмеченные владельцем: цены снятой схемы остаются в форме на
 * случай, что он передумает, но серверу их слать нельзя — такой схемы в карточке уже нет.
 */
export const buildSaveServicePrices = (
  prices: ServicePricesForm,
  shipmentTypes: readonly FulfillmentApi.ShipmentType[]
): FulfillmentApi.SaveServicePrice[] =>
  fulfillmentShipmentTypeOrder
    .filter((shipmentType) => shipmentTypes.includes(shipmentType))
    .flatMap((shipmentType) =>
      (prices[shipmentType] ?? []).map((draft) => ({
        shipment_type: shipmentType,
        service_id: draft.serviceId,
        is_offered: draft.isOffered,
        price_ladder: toPriceLadder(draft, shipmentType),
      }))
    )
